import * as React from "react";
import { push } from "connected-react-router";
import { DragDropContext } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { IAction } from "reduxsauce";
import { Creators as AllCreators } from "../../actions";
import { requestPreviewCourseInst, resetPreviewCourseInst } from "../../actions/course-author/course-author";
import { IState } from "../../App/reducer";
import { RequestState } from "../../App/types/common";
import { accountId, accountName, currentCourse, isAdminUser, organizationName } from "../../selectors/selectors";
import { CourseAuthorComponent } from "./CourseAuthor.component";
import { CourseAuthorStateProps, ICourseAuthorDispatchProps, ICourseAuthorProps } from "./CourseAuthor.types";
import { IClientPatch, IPatchAction, IPatchPayload } from "./helpers/patches";
import { patchPayloadCreator } from "./helpers/patches/creators";
import { CourseContent, IFullCourse } from "./types";
import { TabIdContextComponent } from "../../context/TabIdContext";

const Creators = (AllCreators as any).App.CourseAuthor;
const OrgCreators = (AllCreators as any).App.SelectOrganization;
const defaultCourse = {} as IFullCourse;

const mapStateToProps = (state: IState): CourseAuthorStateProps => {
  return {
    authenticationState: state.App._.authenticationState,
    course: state.App.CourseAuthor._.course || defaultCourse,
    courseChanges: state.App.CourseAuthor._.courseChanges || [],
    courseInstId: state.App.CourseAuthor._.courseInstId,
    displayPreviewCourseInstFailMessage: state.App.CourseAuthor._.displayPreviewCourseInstFailMessage,
    fetchCourseRequestError: state.App.CourseAuthor._.fetchCourseRequestError,
    fetchCourseRequestState: state.App.CourseAuthor._.fetchCourseRequestState,
    firstFetch: state.App.CourseAuthor._.course === null,
    inProgress:
      state.App.CourseAuthor._.patchCourseRequestState === RequestState.PENDING ||
      state.App.CourseAuthor._.updateCourseRequestState === RequestState.PENDING ||
      state.App.CourseAuthor._.migrateContentToHtmlRequestState === RequestState.PENDING ||
      state.App.CourseAuthor._.patchCoursePlayerRequestState === RequestState.PENDING,
    isAdmin: isAdminUser(state),
    localizedMessageApi: state.App.CourseAuthor._.localizedMessageApi,
    location: state.router.location,
    multiLocale: state.App.CourseAuthor._.course
      ? currentCourse(state).state.content.type === CourseContent.MultiLocale
      : false,
    organizationName: organizationName(state),
    requestPreviewCourseState: state.App.CourseAuthor._.requestPreviewCourseInstState,
    resolvedData: state.App.CourseAuthor._.resolvedData,
    userId: accountId(state),
    userName: accountName(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<IAction>): ICourseAuthorDispatchProps => ({
  backToCoursesPage: () => dispatch(push("/courses")),
  cloneCourse: (course: IFullCourse, orgId: string, navAction: () => void) =>
    dispatch(Creators.cloneCourse(course, orgId, navAction)),
  createStyle: () => dispatch(Creators.createStyle()),
  migrateContentToHtml: () => dispatch(Creators.migrateContentToHtml()),
  mount: (courseId: string) => {
    dispatch(Creators.mount(courseId));
    dispatch(Creators.fetchCourse(courseId));
    dispatch(Creators.fetchAllOrganizations());
  },
  patchCourse: (versionId: number, patches: IClientPatch<IPatchAction> | Array<IClientPatch<IPatchAction>>) =>
    dispatch(Creators.patchCourse(patchPayloadCreator(versionId, patches))),
  patchStyle: (course: IFullCourse, patchPayload: IPatchPayload) =>
    dispatch(Creators.patchStyle(course.state.style, patchPayload)),
  reduceCourseAmount: () => dispatch(OrgCreators.updateCoursesAmount(-1)),
  requestPreviewCourseInst: (courseId: string) => dispatch(requestPreviewCourseInst({ courseId })),
  resetPreview: () => dispatch(resetPreviewCourseInst()),
  resolve: (key: string, requestData: string[]) => dispatch(Creators.resolveAuxiliaryData(key, requestData)),
  simplePatchCourse: (versionId: number, patches: IClientPatch<IPatchAction> | Array<IClientPatch<IPatchAction>>) =>
    dispatch(Creators.simplePatchCourse(patchPayloadCreator(versionId, patches))),
  unmount: () => dispatch(Creators.unmount()),
});

export const WrappedCourseAuthorComponent = (props: ICourseAuthorProps) => {
  return (
    <TabIdContextComponent>
      <CourseAuthorComponent {...props} />
    </TabIdContextComponent>
  );
};

export const CourseAuthor = connect(
  mapStateToProps,
  mapDispatchToProps
)(DragDropContext(HTML5Backend)(WrappedCourseAuthorComponent));
